import { ACKNOWLEDGEMENT_LOOKBACK_MS } from "@storyboard/dispatch-view/acknowledgement";
import { formatChipTime } from "@/components/feed/tmsEvent/TmsEventChip";
import { tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import type { DriverVolatileRow } from "@/models/repositories/Driver";
import { store } from "@/store/store";
import type { AffectedKeys } from "@/store/useKeyedCompute";
import { type DriverRecentSource, PHOTO_MESSAGE_TEXT } from "@/types/driver";
import {
  type AcknowledgementRequirement,
  feedsOfEventDrivers,
  requiredTmsEvents,
  tmsEventArrivedAt,
} from "./acknowledgements";
import { taskItemText } from "./dispatchItems";
import { NO_TASKS_ELSEWHERE, type TasksElsewhere } from "./tasks";

/**
 * What a driver's row reads about the driver's traffic: whether any feed is unread, the newest thing on them, and
 * whether that newest thing is a message, a required TMS event, or a task pinned to another board.
 */
export type DriverActivity = Pick<DriverVolatileRow, "unread" | "latestItemCreatedAt" | "recentMessage"> & {
  /** Null when the row shows nothing. */
  recentSource: DriverRecentSource | null;
};

/**
 * A driver's newest required TMS event or task pinned to another board, on one alias feed of the driver's, as the
 * driver's row shows it.
 */
export type DriverEvent = {
  /** Whether it is a TMS event or a task. */
  source: "tmsEvent" | "otherBoardTask";
  /** The alias feed the event is shown on. */
  feedId: string;
  driverMembershipId: string;
  /** When the event reached the server, as `tmsEventArrivedAt` gives it; when the task was made. */
  createdAt: string;
  /** The event's chip text, as its thread chip and message row show it; the task's text, as its item row shows it. */
  text: string;
};

/** The collections {@link newestDriverEvent} reads, for `useKeyedCompute`. */
export const driverEventCollections = ["feed", "tmsEvent"] as const;

/** Which alias feeds' newest event a written row can change, for `useKeyedCompute` over {@link driverEventCollections}. */
export const driverEventFeedsAffected: AffectedKeys = {
  feed: (feed) => [feed.id],
  tmsEvent: feedsOfEventDrivers,
};

/**
 * An alias feed's newest TMS event of the kinds `requires` takes, or task pinned to another board when it takes those,
 * within the acknowledgement lookback; null when it has none, or is not a driver's alias feed.
 *
 * Reads the events the way the message-grain rows and the red dot do (`requiredTmsEvents`, `tmsEventArrivedAt`, the
 * chip text), and a task by its `createdAt` and note, so the three agree. On an equal instant the event wins. It never
 * walks messages or folds acknowledgements, since the row needs neither. Which feeds the member may read is the
 * roster's call; this answers for any feed it is handed.
 *
 * @param feedId - The alias feed.
 * @param requires - What the view asks its team to acknowledge.
 * @param now - The clock the lookback is measured against, as epoch milliseconds.
 * @param elsewhere - The open tasks pinned to other boards, by driver; none while tasks are off.
 */
export function newestDriverEvent(
  feedId: string,
  requires: AcknowledgementRequirement,
  now: number,
  elsewhere: TasksElsewhere = NO_TASKS_ELSEWHERE,
): DriverEvent | null {
  const feed = store.get("feed", feedId);
  if (!feed?.isAliasFeed || !feed.aliasMembershipId) return null;

  const driverMembershipId = feed.aliasMembershipId;

  // Keep the newest inside the lookback; anything older is handled by age and never a row. Events are considered
  // first, so an event keeps an equal instant. The row is built only for a candidate that wins.
  const oldest = now - ACKNOWLEDGEMENT_LOOKBACK_MS;
  let newest: DriverEvent | null = null;
  let newestAt = 0;
  const consider = (createdAt: string, make: () => DriverEvent) => {
    const at = Date.parse(createdAt);
    if (at < oldest || (newest !== null && at <= newestAt)) return;
    newest = make();
    newestAt = at;
  };
  for (const event of requiredTmsEvents(driverMembershipId, requires)) {
    const createdAt = tmsEventArrivedAt(event);
    consider(createdAt, () => ({
      source: "tmsEvent",
      feedId,
      driverMembershipId,
      createdAt,
      text: tmsEventChipText(event, formatChipTime),
    }));
  }
  if (requires({ source: "otherBoardTask" })) {
    for (const { task } of elsewhere.get(driverMembershipId) ?? []) {
      consider(task.createdAt, () => ({
        source: "otherBoardTask",
        feedId,
        driverMembershipId,
        createdAt: task.createdAt,
        text: taskItemText(task),
      }));
    }
  }
  return newest;
}

/** What a feed's latest message shows on the driver's row: a photo is described, never named by its file. */
function recentOf(row: DriverVolatileRow): Pick<DriverActivity, "recentMessage" | "recentSource"> {
  if (!row.latestItemCreatedAt) return { recentMessage: row.recentMessage, recentSource: null };
  if (row.recentMessagePhoto) return { recentMessage: PHOTO_MESSAGE_TEXT, recentSource: "photo" };
  return { recentMessage: row.recentMessage, recentSource: "message" };
}

/**
 * Folds a driver's alias feeds, required TMS events, and tasks pinned to other boards into one activity summary per
 * driver.
 *
 * Unread when any feed is. The latest item and its message are the newest of the feeds' latest own messages and the
 * driver's `events`, so a required event that arrives after the driver's last message bumps the row the way a message
 * would. An event never makes a row unread, and on an equal instant the message wins. A photo with no transcription
 * reads as {@link PHOTO_MESSAGE_TEXT}, never its generated file name.
 *
 * @param rows - The driver compute's per-feed rows, already narrowed to the team's channels when a team is in force.
 * @param events - The driver's newest required TMS events, on the same feeds as `rows`.
 */
export function activityByDriver(
  rows: readonly DriverVolatileRow[],
  events: readonly DriverEvent[],
): Map<string, DriverActivity> {
  const byDriver = new Map<string, DriverActivity>();
  for (const row of rows) {
    if (!row.aliasMembershipId) continue;
    const existing = byDriver.get(row.aliasMembershipId);
    if (!existing) {
      byDriver.set(row.aliasMembershipId, {
        unread: row.unread ?? false,
        latestItemCreatedAt: row.latestItemCreatedAt,
        ...recentOf(row),
      });
      continue;
    }
    existing.unread = existing.unread || (row.unread ?? false);
    if (
      row.latestItemCreatedAt &&
      (!existing.latestItemCreatedAt || row.latestItemCreatedAt > existing.latestItemCreatedAt)
    ) {
      existing.latestItemCreatedAt = row.latestItemCreatedAt;
      Object.assign(existing, recentOf(row));
    }
  }

  // Events bump a row only when strictly newer, compared as instants: an event's time and a message's may be
  // formatted differently.
  for (const event of events) {
    const existing = byDriver.get(event.driverMembershipId);
    if (!existing) {
      byDriver.set(event.driverMembershipId, {
        unread: false,
        latestItemCreatedAt: event.createdAt,
        recentMessage: event.text,
        recentSource: event.source,
      });
      continue;
    }
    if (!existing.latestItemCreatedAt || Date.parse(event.createdAt) > Date.parse(existing.latestItemCreatedAt)) {
      existing.latestItemCreatedAt = event.createdAt;
      existing.recentMessage = event.text;
      existing.recentSource = event.source;
    }
  }
  return byDriver;
}
